import { ReactNode } from 'react';
import classNames from 'classnames';
import { compact } from 'lodash';
import * as K from 'common/constants';
import './Toolbar.scss';

type ToolbarProps = {
  items?: Array<ReactNode>;
  leftItems?: Array<ReactNode>;
  rightItems?: Array<ReactNode>;
  fillItemIndex?: number;
  className?: string;
  alignment?: K.ALIGNMENT;
  testId?: string;
};

/**
 * Toolbar is a component that presents a set of related components as a linear row. It takes a list of items
 * and displays them in a single row, adding spacing around them. Items can either be provided in a
 * single list (which will be laid out from left to right), or in two separate lists, one on the left
 * side of the toolbar and the other on the right side.
 *
 * An item can be either a React fragment, or a config object with the follow properties:
 *     @param {Fragment} item    React component/element
 *
 * It is easier to just pass a list of components if you don't need one to fill available space.
 *
 * @prop items            list of toolbar items
 * @prop leftItems        toolbar items to show on left side
 * @prop rightItems       toolbar items to show on right side
 * @prop fillItemIndex    index of item that should fill available space
 * @prop className        class to apply to toolbar
 * @prop alignment        K.ALIGNMENT.{ START | CENTER | END }; default is CENTER
 *
 * @returns {Component}
 */

function Toolbar({
  items,
  leftItems,
  rightItems,
  fillItemIndex,
  className,
  alignment,
  testId,
}: ToolbarProps): JSX.Element {
  const toolbarClassName = classNames('Toolbar', className, {
    'Toolbar--AlignStart': alignment === K.ALIGNMENT.START,
    'Toolbar--AlignEnd': alignment === K.ALIGNMENT.END,
  });

  // wrap each item so we can control layout; give it a key to keep React happy; falsy items are not rendered
  let count = 0;
  const convertItems = (toolbarItems?: Array<ReactNode>, toolbarFillItemIndex?: number): Array<ReactNode> | null =>
    toolbarItems
      ? compact(toolbarItems).map((item, index) => {
          const itemClassName = classNames('Toolbar__Item', { 'Toolbar__Item--Fill': index === toolbarFillItemIndex });
          const key = (className || 'Toolbar') + '-item' + count;
          count += 1;
          return (
            <div className={itemClassName} key={key}>
              {item}
            </div>
          );
        })
      : null;

  const left = convertItems(leftItems || items, fillItemIndex);
  const space = rightItems ? <div className="Toolbar__Space" /> : null;
  const right = convertItems(rightItems, fillItemIndex);

  return (
    <div className={toolbarClassName} data-test-id={testId}>
      {left}
      {space}
      {right}
    </div>
  );
}

export function ToolbarSeparator(): JSX.Element {
  return <div className="ToolbarSeparator">&nbsp;</div>;
}

export default Toolbar;
